import { DateTime } from 'luxon';
import { DateUtility } from './date.utility';

export enum FiltersRelativeDateOperator {

    None = 'None',

    Today = 'Today',
    Yesterday = 'Yesterday',

    On = 'On',
    OnOrBefore = 'On Or Before',
    OnOrAfter = 'On Or After',

    LastXDays = 'Last X Days',
    NextXDays = 'Next X Days',

    ThisWeek = 'This Week',
    ThisMonth = 'This Month',
    ThisYear = 'This Year',

    LastXWeeks = 'Last X Weeks',
    LastXMonths = 'Last X Months',
    LastXYears = 'Last X Year',

    NextXWeeks = 'Next X Weeks',
    NextXMonths = 'Next X Months',
    NextXYears = 'Next X Years',
}

export interface FiltersRelativeDateMethod {
    operator: FiltersRelativeDateOperator;
    value?: Date | number;
    includeCurrent?: boolean;
    timezone?: string;
}

export class FiltersRelativeDateUtility {

    public static getFilter(method: FiltersRelativeDateMethod) {

        const { operator, value } = method;

        const zone = method.timezone
            ? method.timezone
            : 'utc';
        // const timezone = 'utc';

        switch (operator) {

            case FiltersRelativeDateOperator.On: {
                //e.g. user filters 'On' at local time: 15/10/2024-08:00AM (+11 AU)
                //User expected: on this day 15/10/2024
                //Actual query in DB: $gte 14/10/2024-13:00 (UTC) -> $lt 14/10/2024-13:00 (UTC)

                const filterDateLocal = DateUtility.getDateTime(value as any, zone);

                const fromDateUTC = filterDateLocal.startOf('day').toUTC();
                const toDateUTC = fromDateUTC.plus({ days: 1 });

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.OnOrBefore: {
                //e.g. user filters 'On Or Before' at local time: 15/10/2024-08:00AM (+11 AU)
                //User expected: On 15/10/2024 Or less than it
                //Actual DB query: $lt 15/10/2024-13:00 (UTC)

                const filterDateLocal = DateUtility.getDateTime(value as any, zone);

                const toDateUTC = filterDateLocal.endOf('day').toUTC();

                return { $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.OnOrAfter: {
                //e.g. user filters 'OnOrAfter' at local time: 15/10/2024-08:00AM (+11 AU)
                //User expected: On 15/10/2024 Or greater than it
                //Actual DB query: $gte 14/10/2024-13:00 (UTC)

                const filterDateLocal = DateUtility.getDateTime(value as any, zone);

                const fromDateUTC = filterDateLocal.startOf('day').toUTC();

                return { $gte: fromDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.Today: {
                //e.g. user filters 'Today' at local time: 15/10/2024-08:00AM (+11 AU)
                //User expected: $gte 15/10/2024-00:00 (+11 AU) -> $lt 16/10/2024-00:00 (+11 AU)
                //Actual query in DB: $gte 14/10/2024-13:00 (UTC) -> $lt 14/10/2024-13:00 (UTC)

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal.startOf('day').toUTC();
                const toDateUTC = fromDateUTC.plus({ days: 1 });

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.Yesterday: {
                //e.g. user filters 'Yesterday' at local time: 15/10/2024-08:00AM (+11 AU)
                //User expected: $gte 14/10/2024-00:00 (+11 AU) -> $lt 15/10/2024-00:00 (+11 AU)
                //Actual query in DB: $gte 13/10/2024-13:00 (UTC) -> $lt 14/10/2024-13:00 (UTC)

                const filterDateLocal = DateTime.local({ zone }).minus({ days: 1 });

                const fromDateUTC = filterDateLocal.startOf('day').toUTC();
                const toDateUTC = fromDateUTC.plus({ days: 1 });

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.LastXDays: {
                //Dont get trapped, the Last X FiltersRelativeDates should be the opposite of the Next X FiltersRelativeDates
                //not as simple as swiching the plus and minus :)

                // 1st e.g. filter 'Last 3 Days' at local time: 15/10/2024-08:00AM (+11 AU)
                // 1st e.g. Actual query in DB: $gte 11/10/2024-13:00(UTC) -> $lt 14/10/2024-12:59:59(UTC)

                // 2nd e.g. filter 'Last 3 Days include today' at local time: 15/10/2024-08:00AM (+11 AU)
                // 2nd e.g. Actual query in DB: $gte 12/10/2024-13:00(UTC) -> $lt 15/10/2024-12:59:59(UTC)

                const numberOfDays = value as number;
                const includeToday = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('day')
                    .minus({
                        days: includeToday
                            ? numberOfDays - 1
                            : numberOfDays
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('day')
                    .minus({
                        days: includeToday
                            ? 0
                            : 1
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.NextXDays: {
                // 1st e.g. filter 'Next 3 Days' at local time: 15/10/2024-08:00AM (+11 AU)
                // 1st e.g. Actual query in DB: $gte 15/10/2024-13:00(UTC) -> $lt 17/10/2024-12:59:59(UTC)

                // 2nd e.g. filter 'Next 3 Days include today' at local time: 15/10/2024-08:00AM (+11 AU)
                // 2nd e.g. Actual query in DB: $gte 14/10/2024-13:00(UTC) -> $lt 16/10/2024-12:59:59(UTC)

                const numberOfDays = value as number;
                const includeToday = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('day')
                    .plus({
                        days: includeToday
                            ? 0
                            : 1
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('day')
                    .plus({
                        days: includeToday
                            ? numberOfDays - 1
                            : numberOfDays
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.ThisWeek: {
                //e.g. user filters 'ThisWeek' at local time: 15/10/2024-08:00AM (+11 AU)
                // User expected: 14/10/2024-00:00 (+11 AU) -> 19/10/2024-23:59:59:999 (+11 AU)
                //Actual query in DB: $gte 13/10/2024-13:00 (UTC) -> $lt 19/10/2024-12:59:59 (UTC)

                const filterDateLocal = DateTime.local({ zone });

                const startOfWeekUTC = filterDateLocal.startOf('week').toUTC();
                const endOfWeekUTC = filterDateLocal.endOf('week').toUTC();

                return { $gte: startOfWeekUTC.toJSDate(), $lt: endOfWeekUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.NextXWeeks: {
                // 1st e.g. filter 'Next 2 Weeks include this week' at local time: 2024 Oct 4 - 08:00 (+11 AU)
                // 1st e.g. Actual query in DB: $gte 2024 Sep 29 - 13:00:00 (UTC) -> $lt 2024 Oct 13 - 12:59:59:999 (UTC)

                // 2nd e.g. filter 'Next 2 Weeks' at local time: 2024 Oct 4 - 08:00 (+11 AU)
                // 2nd e.g. Actual query in DB: $gte 2024 Oct 06 - 13:00:00 (UTC) -> $lt 2024 Oct 20 - 12:59:59:999 (UTC)

                const numberOfWeeks = value as number;
                const includeThisWeek = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('week')
                    .plus({
                        weeks: includeThisWeek
                            ? 0
                            : 1
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('week')
                    .plus({
                        weeks: includeThisWeek
                            ? numberOfWeeks - 1
                            : numberOfWeeks
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.LastXWeeks: {
                // 1st e.g. filter 'Last 2 Weeks include this week' at local time: 2024 Oct 4 - 08:00 (+11 AU)
                // 1st e.g. expected query on UI: $gte 2024 Sep 23 - 00:00:00 (+11 AU) -> $lt 2024 Oct 06 - 23:59:59:999 (+11 AU)
                // 1st e.g. Actual query in DB: $gte 2024 Sep 22 - 13:00:00 (UTC) -> $lt 2024 Oct 06 - 12:59:59:999 (UTC)

                // 2nd e.g. filter 'Last 2 Weeks' at local time: 2024 Oct 4 - 08:00 (+11 AU)
                // 2nd e.g. expected query on UI: $gte 2024 Sep 16 - 00:00:00 (+11 AU) -> $lt 2024 Sep 29 - 23:59:59:999 (+11 AU)
                // 2nd e.g. Actual query in DB: $gte 2024 Sep 15 - 13:00:00 (UTC) -> $lt 2024 Sep 29 - 12:59:59:999 (UTC)

                const numberOfWeeks = value as number;
                const includeThisWeek = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('week')
                    .minus({
                        weeks: includeThisWeek
                            ? numberOfWeeks - 1
                            : numberOfWeeks
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('week')
                    .minus({
                        weeks: includeThisWeek
                            ? 0
                            : 1
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.ThisMonth: {
                //e.g. user filters 'ThisMonth' at local time: 04/10/2024-08:00AM (+11 AU)
                // User expected: 01/10/2024-00:00 (+11 AU) -> 31/10/2024-23:59:59:999 (+11 AU)
                //Actual query in DB: $gte 30/09/2024-13:00 (UTC) -> $lt 31/10/2024-12:59:59 (UTC)

                const filterDateLocal = DateTime.local({ zone });

                const startOfMonthUTC = filterDateLocal.startOf('month').toUTC();
                const endOfMonthUTC = filterDateLocal.endOf('month').toUTC();

                return { $gte: startOfMonthUTC.toJSDate(), $lt: endOfMonthUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.NextXMonths: {
                const numberOfMonths = value as number;
                const includeThisMonth = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('month')
                    .plus({
                        months: includeThisMonth
                            ? 0
                            : 1
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('month')
                    .plus({
                        months: includeThisMonth
                            ? numberOfMonths - 1
                            : numberOfMonths
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.LastXMonths: {
                const numberOfMonths = value as number;
                const includeThisMonth = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('month')
                    .minus({
                        months: includeThisMonth
                            ? numberOfMonths - 1
                            : numberOfMonths
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('month')
                    .minus({
                        months: includeThisMonth
                            ? 0
                            : 1
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.ThisYear: {
                const filterDateLocal = DateTime.local({ zone });

                const startOfYearUTC = filterDateLocal.startOf('year').toUTC();
                const endOfYearUTC = filterDateLocal.endOf('year').toUTC();

                return { $gte: startOfYearUTC.toJSDate(), $lt: endOfYearUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.NextXYears: {
                const numberOfYears = value as number;
                const includeThisYear = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('year')
                    .plus({
                        years: includeThisYear
                            ? 0
                            : 1
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('year')
                    .plus({
                        years: includeThisYear
                            ? numberOfYears - 1
                            : numberOfYears
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.LastXYears: {
                const numberOfYears = value as number;
                const includeThisYear = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('year')
                    .minus({
                        years: includeThisYear
                            ? numberOfYears - 1
                            : numberOfYears
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('year')
                    .minus({
                        years: includeThisYear
                            ? 0
                            : 1
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }

        }

    }

    public static getLabel(method: FiltersRelativeDateMethod, dateFormat = 'dd/MM/yyyy') {

        if (!method) return null;

        const { operator, value } = method;

        const zone = method.timezone
            ? method.timezone
            : 'utc';

        switch (operator) {

            case FiltersRelativeDateOperator.On: {
                return `On ${DateUtility.formatDateTime(value as any, zone, dateFormat)}`;
            }
            case FiltersRelativeDateOperator.OnOrBefore: {
                return `On Or Before ${DateUtility.formatDateTime(value as any, zone, dateFormat)}`;
            }
            case FiltersRelativeDateOperator.OnOrAfter: {
                return `On Or After ${DateUtility.formatDateTime(value as any, zone, dateFormat)}`;
            }
            case FiltersRelativeDateOperator.Today: {
                return `Today`;
            }
            case FiltersRelativeDateOperator.Yesterday: {
                return `Yesterday`;
            }
            case FiltersRelativeDateOperator.LastXDays: {
                return `Last ${value} Days`;
            }
            case FiltersRelativeDateOperator.NextXDays: {
                return `Next ${value} Days`;
            }
            case FiltersRelativeDateOperator.ThisWeek: {
                return `This Week`;
            }
            case FiltersRelativeDateOperator.NextXWeeks: {
                return `Next ${value} Weeks`;
            }
            case FiltersRelativeDateOperator.LastXWeeks: {
                return `Last ${value} Weeks`;
            }
            case FiltersRelativeDateOperator.ThisMonth: {
                return `This Month`;
            }
            case FiltersRelativeDateOperator.NextXMonths: {
                return `Next ${value} Months`;
            }
            case FiltersRelativeDateOperator.LastXMonths: {
                return `Last ${value} Months`;
            }
            case FiltersRelativeDateOperator.ThisYear: {
                return `This Year`;
            }
            case FiltersRelativeDateOperator.NextXYears: {
                return `Next ${value} Years`;
            }
            case FiltersRelativeDateOperator.LastXYears: {
                return `Last ${value} Years`;
            }

        }
    }
}
